/**
 * Width budgets for axis labels, in `ch` so they map one-to-one onto the
 * character caps the module schema hands the LLM.
 */
export const AXIS_LABEL_BUDGET_CH = {
  /** Numeric y-axis ticks ("1.2k", "-18%"). */
  yAxisTick: 8,
  /** Category names on the y-axis of a horizontal bar chart. */
  yAxisCategory: 16,
  /** X-axis tick labels on every cartesian chart. */
  xAxisTick: 14,
  /**
   * Caption naming a whole axis, parked in the y-rail. The rail is dead width
   * for the plot: at the 375px reference viewport a module has ~343px of row,
   * of which the numeric ticks already claim ~57px. 10ch (~61px) plus the 8px
   * gutter keeps the rail at a fifth of the row while still fitting a word
   * like "Buchungszeitraum" in two wrapped lines.
   */
  axisName: 10,
} as const;

export type AxisLabelBudget = keyof typeof AXIS_LABEL_BUDGET_CH;

/** Lines an axis label may wrap to before it is clipped. */
export const AXIS_LABEL_MAX_LINES = 3;

/**
 * Floor for the pixel cap. A cap narrower than this cannot express "do not
 * collide with the neighbour" any more — it only shreds the label into one
 * glyph per line, and at zero it erases it. Below the floor the slot is simply
 * too tight for any label, which is a signal that the plot has to grow and
 * scroll sideways, not a licence to delete information.
 */
export const AXIS_LABEL_MIN_CAP_CH = 3;

/**
 * CSS `max-width` for an axis label: the character budget, further capped by
 * `pxCap` when the slot the label sits in (band, column) is narrower — never
 * below {@link AXIS_LABEL_MIN_CAP_CH}. Only valid for HTML labels; SVG `<text>`
 * cannot wrap and needs a single-line budget instead.
 */
export function axisLabelMaxWidth(
  budget: AxisLabelBudget,
  pxCap?: number
): string {
  const budgetCh = AXIS_LABEL_BUDGET_CH[budget];
  const ch = `${budgetCh}ch`;
  if (pxCap == null || !Number.isFinite(pxCap)) {
    return ch;
  }
  const floorCh = Math.min(AXIS_LABEL_MIN_CAP_CH, budgetCh);
  return `clamp(${floorCh}ch, ${Math.max(0, pxCap)}px, ${ch})`;
}
